



















































import {Component, Mixins} from 'vue-mixin-decorator';
import {Prop, Vue, Watch} from 'vue-property-decorator';
import {getModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Notify} from '@/utils/Notify';
import {EventBus} from '@/utils/EventBus';
import {Time} from '@/utils/Time';

import {Profile} from '@/model/network/profile/Profile';
import {IpAddressingMethod} from '@/model/network/IpAddressingMethod';

import {IApiService} from '@/services/IApi.service';
import {INetworkProfileService} from '@/services/network/INetworkProfileService';
import {INetworkNetworkingService} from '@/services/network/INetworkNetworkingService';

import NetworkProfilesStore from '@/store/network/NetworkProfiles.store';
import {ApiStore} from '@/store/Api.store';

import {BaseComponent} from '@/mixins/BaseComponent';
import NetworkProfileEditor from '@/components/Networking/Profile/NetworkProfileEditor.vue';
import PropertyDisplayer from '@/components/Utils/PropertyDisplayer.vue';
import {NetworkRestoreProcedure} from '@/components/Networking/NetworkRestoreProcedure';
import NetworkVpnSettingsEditor from '@/components/Networking/Vpn/NetworkVpnSettingsEditor.vue';
import SafeUpdateDialog from '@/components/Networking/SafeUpdate/SafeUpdateDialog.vue';
import {SafeOperation} from '@/services/SafeOperation';
import {Level} from '@/model/Notification';
import SafeUpdateSettings from '@/components/Networking/SafeUpdate/SafeUpdateSettings.vue';
import NetworkConnectionsStore from '@/store/network/NetworkConnections.store';
import NetworkDevicesStore from '@/store/network/NetworkDevices.store';
import {INetworkDeviceService} from '@/services/network/INetworkDeviceService';
import {Connection} from '@/model/network/connection/Connection';

@Component({
  components: {
    SafeUpdateSettings,
    SafeUpdateDialog,
    NetworkVpnSettingsEditor,
    PropertyDisplayer,
    NetworkProfileEditor,
  },
})
export default class NetworkVpnSettingsEditorModal extends Mixins<BaseComponent>(BaseComponent) {

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  @Inject(Symbol.for('INetworkNetworkingService'))
  private networkingService!: INetworkNetworkingService;

  @Inject(Symbol.for('INetworkProfileService'))
  private profileService!: INetworkProfileService;

  @Inject(Symbol.for('INetworkDeviceService'))
  private deviceService!: INetworkDeviceService;

  private apiStore: ApiStore = getModule(ApiStore, this.$store);

  private profileStore: NetworkProfilesStore = getModule(NetworkProfilesStore, this.$store);

  private connectionStore: NetworkConnectionsStore = getModule(NetworkConnectionsStore, this.$store);

  private deviceStore: NetworkDevicesStore = getModule(NetworkDevicesStore, this.$store);

  @Prop()
  private interface!: string;

  @Prop({default: null})
  private activeProfile!: Profile | null;

  private editedProfile: Profile | null = null;

  private saving: boolean = false;

  private safe: boolean = true;

  private host: string = '';

  private IpAddressingMethod = IpAddressingMethod;

  get hostGuess(): string {

    switch (this.editedProfile?.Settings.IPv4?.Method) {
      case IpAddressingMethod.Manual:

        const addresses = this.editedProfile?.Settings.IPv4?.Addresses ?? null;

        if (!addresses || addresses.length === 0) {
          return this.apiStore.Remote;
        }

        return addresses[0].Address;

      default:
        return this.apiStore.Remote;
    }
  }

  get connection(): Connection | undefined {
    return this.connectionStore.ConnectionsVpn?.find((c) => c.UUID === this.editedProfile?.Settings.Connection.Uuid);
  }

  get isConnected(): boolean {
    return this.connection !== undefined;
  }

  private async mounted() {
    this.onActiveProfileChanged();
  }

  private async close() {
    this.$emit('close', true);
  }

  @Watch('activeProfile')
  private onActiveProfileChanged() {
    if (this.activeProfile) {
      this.editedProfile = new Profile(this.activeProfile);
    } else {
      this.editedProfile = null;
    }
    this.logger.trace('Edited profile is now', this.editedProfile);
  }

  public validate(): boolean {
    return (this.$refs.settingsEditor as Vue & { validate: () => boolean }).validate();
  }

  private async saveInactive() {

    if (!this.validate()) {
      Notify.Error(this.logger, 'Settings error');
      return;
    }

    if (!this.activeProfile || !this.activeProfile.Id) {
      return;
    }

    if (!this.editedProfile || !this.editedProfile.Id) {
      return;
    }

    this.saving = true;
    try {
      await this.save();
      Notify.Info(this.logger, `The settings have been saved`);
      this.close();
    } catch (error) {
      Notify.Error(this.logger, `Could not save settings`, error);
    }
    this.saving = false;
  }

  private async saveConnect() {

    if (!this.validate()) {
      Notify.Error(this.logger, 'Settings error');
      return;
    }

    this.logger.trace(`connect`);

    this.saving = true;
    try {
      /* If safe update is enabled, create a restore procedure */
      if (this.safe) {
        const safeOperation = new SafeOperation(this.networkingService, this.apiService);
        await safeOperation.Execute(
            this.connect,
            this.save,
            (level: Level, message: string, details?: string[], error?: Error) => {
              Notify.Notify(this.logger, level, message, details, error);
            },
            this.host,
        );
      } else {
        await this.save();
        await this.connect();
      }

      Notify.Success(this.logger, `The settings have been successfully updated.`);
      this.close();
    } catch (error) {
      if (error.message) {
        Notify.Error(this.logger, error.message);
      }
    }
    this.saving = false;
  }

  private async save() {
    this.logger.debug(`Saving edited profile`);
    const profileToSave = new Profile(this.editedProfile!);
    await this.updateProfile(profileToSave, this.activeProfile!);
  }

  private async connect() {
    /* Connect the new profile */
    try {
      this.logger.debug(`Connecting new profile`, this.editedProfile!.Id);
      await this.profileService.connect(this.editedProfile!.Id);
    } catch (error) {
      // TODO: only catch timeouts
      if (error.code === 500) {
        this.logger.error('Could not connect profile', error);
        throw error;
      } else {
        this.logger.warn(`Could not connect the profile, if it's a timeout, this is probably normal as we just disconnected the active connection`, error);
      }
    }
  }

  /**
   * Update a profile on the gateway and in the store if successful
   * Handles also the case where the ID changes
   * @param updatedProfile The profile to update
   * @param originalProfile The profile how it was before edition
   */
  private async updateProfile(updatedProfile: Profile, originalProfile?: Profile) {
    this.logger.trace('Updating profile', updatedProfile);

    const originalId = originalProfile ? originalProfile.Id : updatedProfile.Id;
    const profile = await this.profileService.update(originalId, updatedProfile);
    this.logger.trace('Profile updated', profile);

    /* Update the store */
    if (originalProfile && profile.Id !== originalProfile.Id) {
      this.logger.debug('Profile ID has changed, updating store', originalId, profile.Id);
      this.profileStore.removeProfile(originalProfile);
      this.profileStore.addProfile(profile);
    } else {
      this.logger.debug(`Updating profile in store`, profile);
      this.profileStore.setProfile(profile);
    }
  }
}

